import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  AttendanceRecordDto,
  AttendanceSummaryDto,
  Paginated,
  TodayAttendanceDto,
  UpsertAttendanceDto,
} from '@hrms/shared';
import { get, post, postForm, toApiErrorMessage } from '@/lib/api-client';
import type { PunchCaptureResult } from './punch-capture-dialog';
import { locationKeys } from '@/features/location/location-api';

/**
 * A punch as multipart.
 *
 * Optional numbers are omitted entirely rather than sent empty: an empty form
 * field arrives as `''`, and coercing that yields 0 — a real coordinate off
 * West Africa, written onto the record as though someone had stood there.
 */
function punchForm(capture: PunchCaptureResult): FormData {
  const form = new FormData();
  // The field is absent rather than empty when there is no photo: multer treats
  // an empty file part as a zero-byte upload, which the server would reject as
  // an empty photo instead of reading it as "none sent".
  if (capture.photo) {
    form.append('photo', capture.photo, 'punch.jpg');
  }
  form.append('deviceReportedAt', capture.deviceReportedAt);

  if (capture.latitude !== null) form.append('latitude', String(capture.latitude));
  if (capture.longitude !== null) form.append('longitude', String(capture.longitude));
  if (capture.accuracyMeters !== null) {
    form.append('accuracyMeters', String(capture.accuracyMeters));
  }

  return form;
}

export const attendanceKeys = {
  all: ['attendance'] as const,
  today: () => [...attendanceKeys.all, 'today'] as const,
  list: (params: Record<string, unknown>) => [...attendanceKeys.all, 'list', params] as const,
  summary: (params: Record<string, unknown>) => [...attendanceKeys.all, 'summary', params] as const,
};

export function useToday(enabled = true) {
  return useQuery({
    queryKey: attendanceKeys.today(),
    queryFn: () => get<TodayAttendanceDto>('/attendance/today'),
    enabled,
    // The clock is only meaningful if it is current — a stale "not clocked in"
    // after someone punched on another device is worse than a brief spinner.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useAttendance(params: Record<string, unknown>) {
  return useQuery({
    queryKey: attendanceKeys.list(params),
    queryFn: () => get<Paginated<AttendanceRecordDto>>('/attendance', { params }),
    placeholderData: (previous) => previous,
  });
}

export function useAttendanceSummary(params: Record<string, unknown>) {
  return useQuery({
    queryKey: attendanceKeys.summary(params),
    queryFn: () => get<AttendanceSummaryDto>('/attendance/summary', { params }),
    placeholderData: (previous) => previous,
  });
}

function useAttendanceInvalidation() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
}

/**
 * Tells the location reporter that the shift just started or ended.
 *
 * Whether a device should report is answered by `/location/state`, which is
 * cached and refetched once a minute. Clocking in flips that answer, and
 * without this the reporter did not find out for up to sixty seconds — so
 * somebody who punched and pocketed their phone inside that minute never
 * reported at all. Two people did exactly that on the first day.
 *
 * The reverse matters as much: clocking out should stop reporting promptly
 * rather than leaving a device sending positions after the shift ended.
 */
function useLocationStateInvalidation() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: locationKeys.all });
}

export function useClockIn() {
  const invalidate = useAttendanceInvalidation();
  const refreshLocationState = useLocationStateInvalidation();

  return useMutation({
    mutationFn: (capture: PunchCaptureResult) =>
      postForm<AttendanceRecordDto>('/attendance/clock-in', punchForm(capture)),
    onSuccess: (record) => {
      invalidate();
      refreshLocationState();
      toast.success(
        record.lateMinutes > 0
          ? `Clocked in — ${record.lateMinutes} minute(s) late`
          : 'Clocked in',
      );
    },
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useClockOut() {
  const invalidate = useAttendanceInvalidation();
  const refreshLocationState = useLocationStateInvalidation();

  return useMutation({
    // Takes the same capture as clocking in. The end of the shift is what
    // decides the hours paid, and an arrival photo says nothing about where
    // somebody was when they stopped.
    mutationFn: async (capture: PunchCaptureResult) =>
      postForm<AttendanceRecordDto>('/attendance/clock-out', punchForm(capture)),
    onSuccess: (record) => {
      invalidate();
      refreshLocationState();
      const hours = Math.floor(record.workedMinutes / 60);
      const minutes = record.workedMinutes % 60;
      toast.success(`Clocked out — ${hours}h ${minutes}m worked`);
    },
    onError: (error) => toast.error(toApiErrorMessage(error)),
  });
}

export function useUpsertAttendance() {
  const invalidate = useAttendanceInvalidation();

  return useMutation({
    mutationFn: (body: UpsertAttendanceDto) =>
      post<AttendanceRecordDto>('/attendance/records', body),
    onSuccess: () => {
      invalidate();
      toast.success('Attendance recorded');
    },
    onError: (error) => {
      toast.error(toApiErrorMessage(error));
      throw error;
    },
  });
}
